import api from './api';
import { IS_MOCK_MODE, mockApiResponse } from './mockData';

// All college portal API calls hit POST /college/{action} on API Gateway.
// Backend dispatches by `action` field in the body.
// In mock mode (REACT_APP_MOCK_MODE=true), short-circuits to mockData.js so
// reviewers can click through the UI without a live backend or Cognito session.
const collegePost = (action, params = {}) => {
  if (IS_MOCK_MODE) return mockApiResponse(action, params);
  return api.post(`/college/${action}`, { action, ...params }).then((res) => res.data);
};

// ─── Institution ──────────────────────────────────────────────────────────────
export const getInstitution = (collegeId) =>
  collegePost('get_institution', { college_id: collegeId });

export const updateInstitutionSettings = (collegeId, settings) =>
  collegePost('update_institution_settings', { college_id: collegeId, ...settings });

// Audit trail of past settings changes.
export const getSettingsChangelog = (collegeId, limit = 50) =>
  collegePost('get_settings_changelog', { college_id: collegeId, limit });

// Preview the impact of proposed settings changes BEFORE saving — returns
// affected student count, how many may move to at-risk, etc.
export const previewSettingsImpact = (collegeId, proposedSettings) =>
  collegePost('preview_settings_impact', { college_id: collegeId, ...proposedSettings });

export const uploadInsuranceCertificate = (collegeId, fileName, contentType) =>
  collegePost('get_insurance_upload_url', {
    college_id: collegeId,
    file_name: fileName,
    content_type: contentType,
  });

export const setInsuranceCertificateUrl = (collegeId, publicUrl) =>
  collegePost('set_insurance_certificate', { college_id: collegeId, public_url: publicUrl });

// ─── Dashboard ────────────────────────────────────────────────────────────────
export const getDashboardStats = (collegeId) =>
  collegePost('get_dashboard_stats', { college_id: collegeId });

export const getRecentActivity = (collegeId, limit = 20) =>
  collegePost('get_recent_activity', { college_id: collegeId, limit });

export const getAtRiskStudents = (collegeId, limit = 10) =>
  collegePost('get_at_risk_students', { college_id: collegeId, limit });

// ─── Cohorts ──────────────────────────────────────────────────────────────────
export const listCohorts = (collegeId, status) =>
  collegePost('list_cohorts', { college_id: collegeId, ...(status ? { status } : {}) });

export const getCohort = (cohortId) =>
  collegePost('get_cohort', { cohort_id: cohortId });

export const createCohort = (collegeId, data) =>
  collegePost('create_cohort', { college_id: collegeId, ...data });

export const updateCohort = (cohortId, data) =>
  collegePost('update_cohort', { cohort_id: cohortId, ...data });

export const endCohort = (cohortId) =>
  collegePost('end_cohort', { cohort_id: cohortId });

export const generateEnrollmentLink = (cohortId) =>
  collegePost('generate_enrollment_link', { cohort_id: cohortId });

export const regenerateEnrollmentLink = (cohortId) =>
  collegePost('regenerate_enrollment_link', { cohort_id: cohortId });

// ─── Students ────────────────────────────────────────────────────────────────
export const listAllStudents = (collegeId, filters = {}) =>
  collegePost('list_all_students', { college_id: collegeId, ...filters });

export const listCohortStudents = (cohortId) =>
  collegePost('list_cohort_students', { cohort_id: cohortId });

export const getStudent = (studentId) =>
  collegePost('get_student', { student_id: studentId });

export const getStudentCreditStatus = (studentId, cohortId) =>
  collegePost('get_credit_status', {
    student_id: studentId,
    ...(cohortId ? { cohort_id: cohortId } : {}),
  });

export const setPLACredits = (studentId, cohortId, credits) =>
  collegePost('set_pla_credits', {
    student_id: studentId,
    cohort_id: cohortId,
    pla_credits: credits,
  });

export const setCreditStartDate = (studentId, cohortId, date) =>
  collegePost('set_credit_start_date', {
    student_id: studentId,
    cohort_id: cohortId,
    credit_start_date: date,
  });

// ─── Reports ─────────────────────────────────────────────────────────────────
export const generateTranscriptPdf = (collegeId, studentId, cohortId) =>
  collegePost('generate_transcript_pdf', {
    college_id: collegeId,
    student_id: studentId,
    ...(cohortId ? { cohort_id: cohortId } : {}),
  });

export const generateCohortSummaryPdf = (collegeId, cohortId) =>
  collegePost('generate_cohort_summary_pdf', { college_id: collegeId, cohort_id: cohortId });

export const generateOutcomeReportPdf = (collegeId, cohortId) =>
  collegePost('generate_outcome_report_pdf', { college_id: collegeId, cohort_id: cohortId });

// ─── Course code mappings ────────────────────────────────────────────────────
export const listCourseCodes = (collegeId) =>
  collegePost('list_course_codes', { college_id: collegeId });

export const setCourseCode = (collegeId, mapping) =>
  collegePost('set_course_code', { college_id: collegeId, ...mapping });

export const deleteCourseCode = (collegeId, courseCode) =>
  collegePost('delete_course_code', { college_id: collegeId, course_code: courseCode });

// ─── Billing ─────────────────────────────────────────────────────────────────
export const getBillingPlan = (collegeId) =>
  collegePost('get_billing_plan', { college_id: collegeId });

export const getBillingPeriod = (collegeId) =>
  collegePost('get_billing_period', { college_id: collegeId });

export const listInvoices = (collegeId) =>
  collegePost('list_invoices', { college_id: collegeId });

export const getBillingUsageBreakdown = (collegeId) =>
  collegePost('get_billing_usage_breakdown', { college_id: collegeId });

// Returns a Stripe Customer Portal session URL for managing payment method.
export const getPaymentPortalUrl = (collegeId) =>
  collegePost('get_payment_portal_url', { college_id: collegeId });

export const downloadInvoicePdf = (collegeId, invoiceId) =>
  collegePost('download_invoice_pdf', { college_id: collegeId, invoice_id: invoiceId });

// ─── Observers / faculty ─────────────────────────────────────────────────────
export const listObservers = (collegeId) =>
  collegePost('list_observers', { college_id: collegeId });

export const inviteObserver = (collegeId, email, role) =>
  collegePost('invite_observer', { college_id: collegeId, email, role });

export const revokeObserver = (collegeId, email) =>
  collegePost('revoke_observer', { college_id: collegeId, email });
